/**
 * D-EZ-110 — THE ROOKIE CLASS: scouting reports, and the Rookie of the Year race.
 *
 * Every rookie the winter licensed was rolled on the inaugural tables (SAG/5).
 * His scouting report is a READING of that card — what it says he is good and
 * bad at, measured against the rest of next season's licensed field — plus who
 * he is (the people layer: temperament, where he came up, his hometown) and
 * where he lands (a charter seat or his own car). The scout never guesses how
 * he will do; he says what the card says.
 *
 * The Rookie of the Year race is the standings, read for first-year drivers
 * only, and at the end of the year the award itself (`seasonAwards`).
 */

import type { DriverCard, Quality } from '../types.ts';
import { ageOf } from '../aging.ts';
import { hometownFor, isRealDriver, stableHash } from '../story/hometown.ts';
import { personalityOf } from '../people/context.ts';
import { seasonAwards, type HistorySeason } from '../history/index.ts';
import { asHistory, chapterContext, seasonsThrough, standingsAfter, teamOfIn, type ChapterContext, type ChapterSeason } from './league.ts';
import { count, ordinal, pick, plural, tell, tellOne, rotor, winterQuote, type Said, type Told } from './text.ts';

export type CardTrait = 'speed' | 'defence' | 'escape' | 'qualities' | 'qualifying';

export interface ScoutingReport {
  driverId: string;
  name: string;
  real: boolean;
  age: number;
  hometown: string | null;
  carNumber: string;
  team: string | null;
  /** The card, as printed. */
  card: { gas: number; brake: number; escape: number; qualities: Quality[]; starred: Quality[]; triangle: boolean; trophy: string | null };
  /** Where each number sits in next season's licensed field, 0 (worst) – 1 (best). */
  standing: Record<CardTrait, number>;
  strengths: Told[];
  weaknesses: Told[];
  /** His place in the class on paper, 1 = the pick. */
  classRank: number;
  verdict: Told;
  /** Who he is: temperament words and where he came up (fictional drivers only). */
  traits: string[];
  backstory: string | null;
  lead: Told;
  quote: Said | null;
}

export interface RookieClass {
  /** The winter after this season licensed them; they race in `seasonNumber + 1`. */
  seasonNumber: number;
  raceSeason: number;
  reports: ScoutingReport[];
  intro: Told;
  /** How many start in a charter seat. */
  inCharters: number;
}

const QUALITY_WORDS: Record<Quality, string> = {
  REFLEX: 'reflexes',
  CONTROL: 'car control',
  SKILL: 'skill',
  STAR: 'star power',
  SMART: 'racing smarts',
  HEAT: 'heat',
};

/** Share of the field strictly below `v`, plus half the ties — 0 worst, 1 best. */
function standingOf(v: number, all: readonly number[]): number {
  if (all.length === 0) return 0.5;
  let below = 0;
  let level = 0;
  for (const x of all) {
    if (x < v) below += 1;
    else if (x === v) level += 1;
  }
  return (below + level / 2) / all.length;
}

const TROPHY_SCORE: Record<string, number> = { gold: 3, silver: 2, bronze: 1 };

function cardOf(c: DriverCard): ScoutingReport['card'] {
  return {
    gas: c.gasPedal,
    brake: c.brakePedal,
    escape: c.escape,
    qualities: [...c.qualities],
    starred: [...c.starred],
    triangle: c.triangle,
    trophy: c.trophy,
  };
}

const STRONG = 0.7;
const WEAK = 0.3;

function readCard(r: { driverId: string; card: ScoutingReport['card'] }, standing: Record<CardTrait, number>): { strengths: Told[]; weaknesses: Told[] } {
  const k = (what: string) => `scout|${r.driverId}|${what}`;
  const strengths: Told[] = [];
  const weaknesses: Told[] = [];
  const c = r.card;
  if (standing.speed >= STRONG) {
    strengths.push(tellOne(k('speed+'), ['Real speed: a {gas}-pedal card.', 'Fast — {gas} on the gas pedal, up with the quick ones.', 'The gas pedal reads {gas}. He can get up on the wheel.'], { gas: c.gas }));
  } else if (standing.speed <= WEAK) {
    weaknesses.push(tellOne(k('speed-'), ['Short on raw speed — {gas} on the gas pedal.', 'A {gas}-pedal card: he will have to out-think people to pass them.'], { gas: c.gas }));
  }
  if (standing.defence >= STRONG) {
    strengths.push(tellOne(k('def+'), ['Hard to get by: brake pedal {brake}.', 'Defends like a veteran — a {brake} on the brake pedal.'], { brake: c.brake }));
  } else if (standing.defence <= WEAK) {
    weaknesses.push(tellOne(k('def-'), ['Easy to pass — brake pedal {brake}.', 'With a {brake} brake pedal, the field will get by him on a good roll.'], { brake: c.brake }));
  }
  if (standing.escape >= STRONG) {
    strengths.push(tellOne(k('esc+'), ['Stays out of trouble — escape {escape}.', 'An escape of {escape}: when it goes wrong around him, he usually gets through.'], { escape: c.escape }));
  } else if (standing.escape <= WEAK) {
    weaknesses.push(tellOne(k('esc-'), ['Trouble finds him — escape {escape}.', 'An escape of {escape} means the wrecks will find him.'], { escape: c.escape }));
  }
  const q = c.qualities.length;
  if (standing.qualities >= STRONG) {
    strengths.push(tell('In the fight on {n} of the six race-cam calls ({list}).', { n: count(q), list: c.qualities.map((x) => QUALITY_WORDS[x]).join(', ') }));
  } else if (standing.qualities <= WEAK) {
    weaknesses.push(
      q === 1
        ? tell('Only one quality on the card ({list}) — he needs the race cam to call his number.', { list: QUALITY_WORDS[c.qualities[0]] })
        : tell('{n} qualities ({list}) — the race cam will pass him by a lot of weeks.', { n: count(q), list: c.qualities.map((x) => QUALITY_WORDS[x]).join(', ') || 'none' }),
    );
  }
  for (const s of c.starred) strengths.push(tell('A starred {q}: two cracks at it when the camera calls it.', { q: QUALITY_WORDS[s] }));
  if (c.triangle) weaknesses.push(tell('Carries the warning triangle — a red six in a challenge breaks something.'));
  if (c.trophy === 'gold') strengths.push(tell('A gold-trophy card: he qualifies with the front-runners.'));
  else if (!c.trophy) weaknesses.push(tell('No trophy on the card, so he will start deep in the field most weeks.'));
  return { strengths, weaknesses };
}

/**
 * The rookies the winter after `seasonNumber` licensed, each with a scouting
 * report. Reads the season and its winter; nothing of the season they race in.
 */
export function rookieClass(input: readonly ChapterSeason[], seasonNumber: number, cx?: ChapterContext): RookieClass | null {
  const seasons = seasonsThrough(input, seasonNumber, { winter: true });
  const s = seasons.find((x) => x.seasonNumber === seasonNumber);
  const off = s?.offseason;
  if (!s || !off) return null;
  const everRaced = new Set<string>();
  for (const x of seasons) for (const c of x.roster) everRaced.add(c.driverId);
  const rookieCards: DriverCard[] = off.rookies
    ? off.rookies.map((r) => off.roster.find((c) => c.driverId === r.card.driverId) ?? r.card)
    : off.roster.filter((c) => !everRaced.has(c.driverId));
  if (rookieCards.length === 0) return null;
  const ctx = cx ?? chapterContext(seasons);
  const field = off.roster;
  const fieldStats = {
    speed: field.map((c) => c.gasPedal),
    defence: field.map((c) => c.brakePedal),
    escape: field.map((c) => c.escape),
    qualities: field.map((c) => c.qualities.length + c.starred.length * 0.5),
    qualifying: field.map((c) => TROPHY_SCORE[c.trophy ?? ''] ?? 0),
  };
  const scored = rookieCards.map((c) => {
    const standing: Record<CardTrait, number> = {
      speed: standingOf(c.gasPedal, fieldStats.speed),
      defence: standingOf(c.brakePedal, fieldStats.defence),
      escape: standingOf(c.escape, fieldStats.escape),
      qualities: standingOf(c.qualities.length + c.starred.length * 0.5, fieldStats.qualities),
      qualifying: standingOf(TROPHY_SCORE[c.trophy ?? ''] ?? 0, fieldStats.qualifying),
    };
    const paper = standing.speed * 1.3 + standing.qualities * 1.1 + standing.escape * 0.8 + standing.defence * 0.6 + standing.qualifying * 0.4;
    return { c, standing, paper };
  });
  const order = [...scored].sort((a, b) => b.paper - a.paper || a.c.driverId.localeCompare(b.c.driverId));
  let inCharters = 0;
  const used = new Set<string>();
  const leadTeam = rotor(`rookie-lead|${ctx.leagueKey}|${seasonNumber}|team`);
  const leadOwn = rotor(`rookie-lead|${ctx.leagueKey}|${seasonNumber}|own`);
  const verdictUp = rotor(`rookie-v|${ctx.leagueKey}|${seasonNumber}|up`);
  const verdictDown = rotor(`rookie-v|${ctx.leagueKey}|${seasonNumber}|down`);
  const verdictMid = rotor(`rookie-v|${ctx.leagueKey}|${seasonNumber}|mid`);
  const reports: ScoutingReport[] = order.map(({ c, standing }, i) => {
    const team = teamOfIn(off.teams, c.driverId)?.name ?? null;
    if (team) inCharters += 1;
    const age = ageOf(c);
    const home = hometownFor(c.driverId);
    const real = isRealDriver(c.driverId);
    const persona = personalityOf(c.driverId, ctx.people);
    const card = cardOf(c);
    const { strengths, weaknesses } = readCard({ driverId: c.driverId, card }, standing);
    const classRank = i + 1;
    const key = `rookie|${ctx.leagueKey}|${seasonNumber}|${c.driverId}`;
    const verdict =
      classRank === 1 && order.length > 1
        ? tellOne(`${key}|v`, ['The pick of the class on paper.', 'On the card, the best of this year’s rookies.', 'If you had to bet on one rookie from the card alone, this is him.'], {})
        : classRank === order.length && order.length > 2
          ? tellOne(`${key}|v`, ['A project. The card gives him a lot to learn.', 'The longest road of the class, going by the card.', 'There is not much on the card yet. He will have to earn every spot.'], {})
          : strengths.length > weaknesses.length
            ? verdictUp(['{rank} of {n} in the class on paper — more to like than to worry about.', 'The card has more pluses than minuses: {rank} of the {n} on paper.', 'One to watch. {rank} of {n} on the card, and the strengths are real.'], { rank: ordinal(classRank), n: count(order.length) })
            : weaknesses.length > strengths.length
              ? verdictDown(['{rank} of {n} in the class on paper — rough edges, and a season to smooth them.', 'There is work to do: {rank} of the {n} on paper.', 'The card asks a lot of him. {rank} of {n} in the class.'], { rank: ordinal(classRank), n: count(order.length) })
              : verdictMid(['{rank} of {n} in the class on paper — a middle-of-the-road card.', 'Nothing jumps off the card either way: {rank} of {n}.', 'A steady card, {rank} of the {n} on paper.'], { rank: ordinal(classRank), n: count(order.length) });
    const slots: Record<string, string | number> = { name: c.name, age, car: c.carNumber };
    if (home) slots.home = home;
    if (team) slots.team = team;
    const lead = team
      ? leadTeam(home ? ['{name}, {age}, out of {home}, starts in the {car} for {team}.', '{name} ({age}, {home}) lands the {car} at {team}.', '{team} gives the {car} to {name}, {age}, of {home}.'] : ['{name}, {age}, starts in the {car} for {team}.', '{name} ({age}) lands the {car} at {team}.'], slots)
      : leadOwn(
          home
            ? ['{name}, {age}, out of {home}, starts out in his own car, the {car}.', '{name} ({age}, {home}) runs the {car} without a charter.', 'No charter yet for {name}: the {age}-year-old from {home} brings his own car, the {car}.', '{name} of {home}, {age}, will race the {car} as a privateer.']
            : ['{name}, {age}, starts out in his own car, the {car}.', '{name} ({age}) runs the {car} without a charter.'],
          slots,
        );
    return {
      driverId: c.driverId,
      name: c.name,
      real,
      age,
      hometown: home,
      carNumber: c.carNumber,
      team,
      card,
      standing,
      strengths,
      weaknesses,
      classRank,
      verdict,
      traits: persona.traits,
      backstory: persona.backstory,
      lead,
      quote: winterQuote('rookie', c.driverId, c.name, ctx.people, {}, `${seasonNumber}`, used),
    };
  });
  const intro = reports.length === 1
    ? reports[0].team
      ? tell('Just one rookie licensed this winter, and he has a charter seat: {name} at {team}.', { name: reports[0].name, team: reports[0].team })
      : tell('Just one rookie licensed this winter: {name}, in his own car.', { name: reports[0].name })
    : reports.length === 2 && (inCharters === 0 || inCharters === 2)
      ? inCharters === 0
        ? tell('Two rookies licensed this winter, both starting out in their own cars.')
        : tell('Two rookies licensed this winter, and both have charter seats waiting.')
    : tellOne(
    `rookie-intro|${ctx.leagueKey}|${seasonNumber}`,
    inCharters === 0
      ? ['{n} {rookies} licensed this winter, every one of them starting out in his own car.', 'The class of {n}: no charter seats among them, so it is privateer equipment for all.']
      : inCharters === reports.length
        ? ['{n} {rookies} licensed this winter, and every one of them has a charter seat waiting.', 'The class of {n}, all of them in charter seats from race one.']
        : ['{n} {rookies} licensed this winter; {k} start in charter seats, the rest in their own cars.', 'The class of {n}: {k} with charter rides, {rest} running their own equipment.'],
    { n: count(reports.length), rookies: plural(reports.length, 'rookie'), k: count(inCharters), rest: count(reports.length - inCharters) },
  );
  return { seasonNumber, raceSeason: seasonNumber + 1, reports, intro, inCharters };
}

// ---------------------------------------------------------------------------
// The Rookie of the Year race
// ---------------------------------------------------------------------------

export interface RookieRace {
  seasonNumber: number;
  throughRace: number;
  /** Rookies, best placed first. */
  standings: { driverId: string; name: string; rank: number; points: number; wins: number; top10: number }[];
  /** Settled: the award itself. */
  winner: { driverId: string; name: string; basis: string } | null;
  line: Told | null;
}

/**
 * The first-year drivers of `seasonNumber`, as the standings stood after
 * `throughRace` (inclusive). A settled season reports the award.
 */
export function rookieRace(input: readonly ChapterSeason[], seasonNumber: number, throughRace?: number): RookieRace | null {
  const seasons = seasonsThrough(input, seasonNumber, { winter: false });
  const s = seasons.find((x) => x.seasonNumber === seasonNumber);
  const first = seasons[0]?.seasonNumber ?? seasonNumber;
  if (!s || seasonNumber <= first) return null;
  const earlier = new Set<string>();
  for (const x of seasons) if (x.seasonNumber < seasonNumber) for (const c of x.roster) earlier.add(c.driverId);
  const races = s.season.races.filter((r) => throughRace === undefined || r.raceNumber <= throughRace);
  const last = races[races.length - 1];
  if (!last) return null;
  const settled = s.complete !== false && races.length === s.season.races.length && throughRace === undefined;
  const table = settled ? s.season.finalStandings : standingsAfter(s, last.raceNumber);
  if (!table) return null;
  const standings = table.drivers
    .filter((d) => !earlier.has(d.driverId) && d.starts > 0)
    .map((d) => ({ driverId: d.driverId, name: d.name, rank: d.rank, points: d.points, wins: d.wins, top10: d.top10 }));
  if (standings.length === 0) return null;
  let winner: RookieRace['winner'] = null;
  if (settled) {
    const roty = seasonAwards(asHistory(seasons) as HistorySeason[], seasonNumber, undefined).find((a) => a.kind === 'rookie-of-the-year');
    const top = standings[0];
    winner = roty ? { driverId: roty.driverId, name: roty.name, basis: roty.basis } : { driverId: top.driverId, name: top.name, basis: `best-placed first-year driver — ${ordinal(top.rank)} in the standings` };
  }
  const a = standings[0];
  const b = standings[1];
  const key = `roty|${seasonNumber}|${races.length}`;
  const line = winner
    ? b
      ? tellOne(key, ['{name} is Rookie of the Year, {rank} in the championship — {gap} points clear of {next}.', 'Rookie of the Year: {name}, {rank} in the final standings, ahead of {next}.'], { name: winner.name, rank: ordinal(a.rank), gap: a.points - b.points, next: b.name })
      : tell('{name} is Rookie of the Year, {rank} in the championship.', { name: winner.name, rank: ordinal(a.rank) })
    : b
      ? a.points === b.points
        ? tell('The rookie race is dead level: {name} and {next} on {pts} points each.', { name: a.name, next: b.name, pts: a.points })
        : tellOne(key, ['{name} leads the rookie race, {rank} in points and {gap} points ahead of {next}.', 'Top rookie so far: {name}, {rank} in points — {next} is {gap} points back.'], { name: a.name, rank: ordinal(a.rank), gap: a.points - b.points, next: b.name })
      : tell('{name} is the only rookie to have started a race so far — {rank} in points.', { name: a.name, rank: ordinal(a.rank) });
  return { seasonNumber, throughRace: last.raceNumber, standings, winner, line };
}

/** A stable shuffle-free sample of `n` items, by key — for intros that name a few. */
export function sampleBy<T>(key: string, items: readonly T[], n: number, id: (x: T) => string): T[] {
  return [...items].sort((a, b) => stableHash(`${key}|${id(a)}`) - stableHash(`${key}|${id(b)}`)).slice(0, n);
}

export { pick };
